import type React from "react";

import { Provider } from "jotai";

import { ErrorBoundary } from "@framework/internal/components/ErrorBoundary";
import { DashboardTopic } from "@framework/internal/Dashboard";
import { ImportStatus } from "@framework/Module";
import type { ModuleInstance } from "@framework/ModuleInstance";
import {
    ModuleInstanceTopic,
    ModuleInstanceLifeCycleState,
    useModuleInstanceTopicValue,
} from "@framework/ModuleInstance";
import { StatusSource } from "@framework/ModuleInstanceStatusController";
import { type Workbench } from "@framework/Workbench";
import { Button } from "@lib/components/Button";
import { CircularProgress } from "@lib/components/CircularProgress";
import { usePublishSubscribeTopicValue } from "@lib/utils/PublishSubscribeDelegate";
import { resolveClassNames } from "@lib/utils/resolveClassNames";

import { useActiveSession } from "../../ActiveSessionBoundary";
import { ApplyInterfaceEffectsToSettings } from "../../ApplyInterfaceEffects/applyInterfaceEffects";
import { useDashboard } from "../../DashboardContext";
import { DebugProfiler } from "../../DebugProfiler";
import { HydrateQueryClientAtom } from "../../HydrateQueryClientAtom";

type ModuleSettingsProps = {
    workbench: Workbench;
    moduleInstance: ModuleInstance<any, any>;
};

export const ModuleSettings: React.FC<ModuleSettingsProps> = (props) => {
    const workbenchSession = useActiveSession();
    const importState = useModuleInstanceTopicValue(props.moduleInstance, ModuleInstanceTopic.IMPORT_STATUS);
    const { dashboard, isActive: isActiveDashboard } = useDashboard();

    const activeModuleInstanceId = usePublishSubscribeTopicValue(dashboard, DashboardTopic.ACTIVE_MODULE_INSTANCE_ID);
    const isVisible = isActiveDashboard && activeModuleInstanceId === props.moduleInstance.getId();

    const moduleInstanceLifecycleState = useModuleInstanceTopicValue(
        props.moduleInstance,
        ModuleInstanceTopic.LIFECYCLE_STATE,
    );

    const moduleInstanceSettingsStateInvalid = useModuleInstanceTopicValue(
        props.moduleInstance,
        ModuleInstanceTopic.HAS_INVALID_PERSISTED_SETTINGS,
    );

    if (importState !== ImportStatus.Imported || !props.moduleInstance.isInitialized()) {
        return null;
    }

    if (
        moduleInstanceLifecycleState === ModuleInstanceLifeCycleState.INITIALIZING ||
        moduleInstanceLifecycleState === ModuleInstanceLifeCycleState.RESETTING
    ) {
        const text =
            moduleInstanceLifecycleState === ModuleInstanceLifeCycleState.INITIALIZING
                ? "Initializing..."
                : "Resetting...";
        return (
            <div
                className="mx-2xs my-2xs h-full w-full flex-col items-center justify-center"
                style={{ display: isVisible ? "flex" : "none" }}
            >
                <CircularProgress />
                <div className="mt-xs">{text}</div>
            </div>
        );
    }

    if (moduleInstanceLifecycleState === ModuleInstanceLifeCycleState.ERROR) {
        const errorObject = props.moduleInstance.getFatalError();
        if (errorObject) {
            return (
                <div
                    className="mx-2xs my-2xs text-danger-subtle"
                    style={{
                        display: isVisible ? "flex" : "none",
                    }}
                >
                    This module instance has encountered an error. Please see its view for more details.
                </div>
            );
        }
    }

    function makeContent() {
        if (moduleInstanceSettingsStateInvalid) {
            return (
                <div className="gap-x-xs flex h-full w-full flex-col items-center justify-center">
                    <div className="my-2xs text-danger-subtle text-center">
                        The saved settings state for this module is invalid and could not be applied. It has most likely
                        been outdated by a module update. You can reset the module to its default values to continue
                        using it.
                    </div>
                    <Button onClick={() => props.moduleInstance.resetInvalidPersistedFlags()} variant="contained">
                        Reset module
                    </Button>
                </div>
            );
        }

        const atomStore = workbenchSession
            .getAtomStoreMaster()
            .getAtomStoreForModuleInstance(props.moduleInstance.getId());

        if (!atomStore) {
            return null;
        }

        return (
            <DebugProfiler
                id={`${props.moduleInstance.getId()}-settings`}
                source={StatusSource.Settings}
                statusController={props.moduleInstance.getStatusController()}
                guiMessageBroker={props.workbench.getGuiMessageBroker()}
            >
                <Provider store={atomStore}>
                    <HydrateQueryClientAtom>
                        <ApplyInterfaceEffectsToSettings moduleInstance={props.moduleInstance}>
                            {/* eslint-disable-next-line @eslint-react/static-components -- Legacy */}
                            <Settings
                                settingsContext={props.moduleInstance.getContext()}
                                workbenchSession={props.workbench.getSessionManager().getActiveSession()}
                                syncSettingsService={dashboard.getSyncSettingsService()}
                                workbenchSettings={props.workbench
                                    .getSessionManager()
                                    .getActiveSession()
                                    .getWorkbenchSettings()}
                                initialSettings={props.moduleInstance.getInitialSettings() || undefined}
                            />
                        </ApplyInterfaceEffectsToSettings>
                    </HydrateQueryClientAtom>
                </Provider>
            </DebugProfiler>
        );
    }

    // eslint-disable-next-line @eslint-react/static-components -- Legacy
    const Settings = props.moduleInstance.getSettingsFC();
    return (
        <div
            key={props.moduleInstance.getId()}
            className={resolveClassNames(isVisible ? "flex flex-col" : "hidden", "relative h-full min-h-0 w-full grow")}
            style={{ contain: "content" }}
        >
            <ErrorBoundary moduleInstance={props.moduleInstance}>
                <div className="flex min-h-0 grow flex-col">{makeContent()}</div>
            </ErrorBoundary>
        </div>
    );
};
